import { Select, type SwitchData, Switches } from 'mui-rff';
import React, { useMemo, useState } from 'react';

import Form from '@dashboard/components/form/Form';
import {
  usePlatformCountsQuery,
  usePlatformQuery,
  useSystemConfigurationQuery,
  useUpdatePlatformMutation,
} from '@dashboard/generated/wp/administrator';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack } from '@mui/material';

import parseFormSchema, { getInitialValues } from './parseFormSchema';

const FEATURE_FLAGS: SwitchData[] = [
  { label: 'Raw template previews', value: 'rawTemplatePreviews' },
  { label: 'Legacy templates (Outlook)', value: 'legacyTemplates' },
];

type Props = {
  platformId: string;
};

export default function EditPlatformDialog({ platformId }: Props) {
  const [open, setOpen] = useState(false);
  const { data: platformData, refetch: refetchPlatformData } = usePlatformQuery({ variables: { id: platformId } });
  const { data: countsData, refetch: refetchCountsData } = usePlatformCountsQuery({ variables: { id: platformId } });
  const { data: systemConfigurationData } = useSystemConfigurationQuery();
  const [executeUpdate, { loading: updateLoading }] = useUpdatePlatformMutation();

  const ipPoolNames = systemConfigurationData?.SystemConfiguration.availableSesIpPools;

  const loading = updateLoading || platformData === undefined || countsData === undefined || ipPoolNames === undefined;

  const renderIpPoolMenuItems = () => {
    if (!ipPoolNames) {
      // Placeholder
      return <div style={{ height: 53.13, marginTop: 16, marginBottom: 8 }} />;
    }
    return (
      <Select name="ipPoolName" label="IP pool name" formControlProps={{ margin: 'normal' }}>
        <MenuItem value="default"> - </MenuItem>
        {ipPoolNames.map((poolName) => (
          <MenuItem key={poolName} value={poolName}>
            {poolName}
          </MenuItem>
        ))}
      </Select>
    );
  };

  const initialValues = useMemo(() => {
    if (platformData && countsData) {
      return getInitialValues(platformData.Platform, countsData.Platform);
    }
    return null;
  }, [platformData, countsData]);

  const handleClose = () => {
    setOpen(false);
  };
  const handleOpen = () => {
    setOpen(true);
  };

  if (initialValues === null) {
    return (
      <Button disabled type="button">
        Edit
      </Button>
    );
  }

  const onSubmit = async (uData: unknown) => {
    const data = parseFormSchema(uData);
    if (data === null) {
      alert('Invalid data in form');
      return;
    }
    await executeUpdate({ variables: { id: platformId, data } });
    await refetchPlatformData();
    await refetchCountsData();
    handleClose();
  };

  return (
    <>
      <Button type="button" onClick={handleOpen}>
        Edit
      </Button>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Edit platform information</DialogTitle>
        <Form initialValues={initialValues} onSubmit={onSubmit}>
          <DialogContent>
            <Stack spacing={1}>
              {renderIpPoolMenuItems()}

              <Select
                name="waypointSendingStrategy"
                label="Waypoint sending strategy"
                formControlProps={{ margin: 'normal' }}
              >
                <MenuItem value="DISABLED">DISABLED</MenuItem>
                <MenuItem value="PAUSED">PAUSED</MenuItem>
                <MenuItem value="STANDARD">STANDARD</MenuItem>
                <MenuItem value="HIGH_RISK">HIGH_RISK</MenuItem>
                <MenuItem value="HIGH_VOLUME">HIGH_VOLUME</MenuItem>
              </Select>
              <Switches
                name="featureFlags"
                label="Feature flags"
                formControlProps={{ margin: 'normal' }}
                data={FEATURE_FLAGS}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={loading} onClick={handleClose} color="secondary">
              Cancel
            </Button>
            <Button disabled={loading} type="submit" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Form>
      </Dialog>
    </>
  );
}
